import React, {useEffect, useState} from "react";
import {Box, ButtonBase, Stack, Typography} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {useNavigate} from "react-router-dom";
import {PendingEntry, PendingItemAudienceEnum, PendingItemSeverityEnum, PendingRestControllerApiFactory} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {EmptyState, Panel, SkeletonRows, SoftChip, Tone} from "../../../components/kit";

const SEVERITY: Record<string, { label: string, tone: Tone }> = {
	[PendingItemSeverityEnum.OVERDUE]: {label: "Vencido", tone: "error"},
	[PendingItemSeverityEnum.SOON]: {label: "Pronto", tone: "warning"},
	[PendingItemSeverityEnum.INFO]: {label: "Aviso", tone: "info"},
};
const ORDER = [PendingItemSeverityEnum.OVERDUE, PendingItemSeverityEnum.SOON, PendingItemSeverityEnum.INFO] as string[];

const isHr = (e: PendingEntry) => e.item?.audience === PendingItemAudienceEnum.HR
	|| (e.item?.alsoFor || []).some(a => a === PendingItemAudienceEnum.HR);

/**
 * Los avisos de RR. HH. del motor de pendientes (#14099) en la portada de RR. HH.: hasta ahora solo
 * salían en «Lo que pide atención» (/app/admin), a la que no lleva ningún menú, y quien entraba por
 * RR. HH. no los veía. Aquí van agrupados por tipo y cada grupo abre el tablero filtrado,
 * donde se ven uno a uno y se marcan como hechos o se aplazan.
 */
export const HrPendingPanel = () => {
	const apiManager = useApiManager();
	const navigate = useNavigate();
	const [groups, setGroups] = useState<Group[]>();
	const [error, setError] = useState(false);

	useEffect(() => {
		// «all» solo lo atiende el servidor para ADMIN; así dirección también ve lo de RR. HH.
		apiManager.execute(apiManager.factory(PendingRestControllerApiFactory).boardPending(true))
			.then(b => setGroups(group((b.entries || []).filter(isHr))))
			.catch(() => setError(true));
	}, []);

	return (
		<Panel title="Avisos de RR. HH." subtitle="Lo que vence o falta, por tipo. Ábrelo para verlo uno a uno, marcarlo como hecho o aplazarlo."
			   icon={<NotificationsActiveRoundedIcon fontSize="small" color="action"/>}>
			{error ? (
				<EmptyState title="No se han podido cargar los avisos" description="Vuelve a cargar la página."/>
			) : !groups ? <SkeletonRows rows={4} height={44}/> : groups.length === 0 ? (
				<EmptyState title="Ningún aviso de RR. HH."/>
			) : (
				<Box sx={{display: "grid", gap: 1.5,
					gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))"}}}>
					{groups.map(g => {
						const sev = SEVERITY[g.worst];
						return (
							<ButtonBase key={g.source} onClick={() => navigate("/app/admin?pendiente=" + encodeURIComponent(g.source))}
										sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1,
											textAlign: "left", p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider"}}>
								<Box sx={{minWidth: 0}}>
									<Stack direction="row" spacing={1} alignItems="center">
										<Typography variant="body2" fontWeight={600}>{g.label}</Typography>
										<SoftChip tone={sev.tone} label={String(g.total)}/>
									</Stack>
									<Typography variant="caption" color="text.secondary" sx={{
										display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"}}>
										{g.first}
									</Typography>
								</Box>
								<ChevronRightRoundedIcon fontSize="small" color="action" sx={{flexShrink: 0, mt: 0.25}}/>
							</ButtonBase>
						);
					})}
				</Box>
			)}
		</Panel>
	);
};

interface Group {
	source: string;
	label: string;
	total: number;
	worst: string;
	first: string;
}

/** Un grupo por fuente, del más urgente al menos; de cada uno, el título del aviso más urgente. */
const group = (entries: PendingEntry[]): Group[] => {
	const rank = (s?: string) => ORDER.indexOf(s || PendingItemSeverityEnum.INFO);
	const m = new Map<string, Group>();
	[...entries].sort((a, b) => rank(a.item?.severity) - rank(b.item?.severity)).forEach(e => {
		const k = e.item?.source || "";
		const g = m.get(k);
		if (g) g.total++;
		else m.set(k, {source: k, label: e.item?.sourceLabel || k, total: 1,
			worst: e.item?.severity || PendingItemSeverityEnum.INFO, first: e.item?.title || ""});
	});
	return [...m.values()].sort((a, b) => rank(a.worst) - rank(b.worst) || b.total - a.total);
};
